import { useMemo } from "react";
import { useGetOrdersQuery } from "src/api/services/ordersApi";
import { ProductCategories, ProductsWithMedicationForm } from "src/api/types";
import { useLatestPrescription } from "src/context/LatestPrescriptionProvider";
import { usePatientSubscriptions } from "src/context/PatientSubscriptionsProvider";

type Results = {
  subscriptionProducts: ProductsWithMedicationForm[];
  oneTimeProducts: ProductsWithMedicationForm[];
};

export const useFetchCarouselProducts = (
  products: ProductsWithMedicationForm[] = [],
): Results => {
  const category = usePatientSubscriptions((sub) =>
    sub.map((s) => s.product_category.name),
  );

  const wlSubscription = usePatientSubscriptions((sub) =>
    sub.find((s) => s.product_category.name === ProductCategories.WeightLoss),
  );
  const { data: orders = [] } = useGetOrdersQuery();

  const alwaysDisplay = useMemo(() => new Set(["ec", "on", "b-ot"]), []);

  const prescription = useLatestPrescription((p) => {
    return p.status === "created" ? p : null;
  });

  const weightLossOrder = useMemo(() => {
    return orders.filter(
      (o) =>
        o.product_category.name === ProductCategories.WeightLoss &&
        o.status !== "canceled",
    );
  }, [orders]);

  const subscriptionProducts = useMemo(() => {
    return products.filter((product) => {
      if (product.billing_model === "one_time") return false;

      // 1) hide if already subscribed to this category
      if (
        category.includes(product.category.name) &&
        wlSubscription?.status !== "canceled"
      )
        return false;

      // 2) hide if there’s a created prescription for this exact product
      const rxProduct = prescription && prescription.product_category.name;

      if (prescription && rxProduct === product.category.name) {
        return false;
      }

      // otherwise show it
      return true;
    });
  }, [category, prescription, products, wlSubscription?.status]);

  const oneTimeProducts = useMemo(() => {
    return products.filter((product) => {
      if (product.billing_model === "subscription") return false;

      if (alwaysDisplay.has(product.slug)) return true;

      // 1) hide if already subscribed to this category
      if (
        category.includes(product.category.name) &&
        wlSubscription?.status !== "canceled"
      )
        return false;

      if (
        weightLossOrder.find(
          (o) => o.product.slug === product.slug && o.status !== "delivered",
        )
      ) {
        return false;
      }

      // 2) hide if there’s a created prescription for this exact product
      const rxProduct = prescription && prescription.product_category.name;

      if (prescription && rxProduct === product.category.name) {
        return false;
      }

      // otherwise show it
      return true;
    });
  }, [
    alwaysDisplay,
    category,
    prescription,
    products,
    weightLossOrder,
    wlSubscription?.status,
  ]);

  return { subscriptionProducts, oneTimeProducts };
};
